import { useEffect, useMemo, useState } from "react";
import "./GroupList.css";
import {
  redeemOffer,
  getAllOffers,
  getOffersContact,
  getOfferRegions,
} from "../../api/api-hook";
import { goToPurchasePage } from "../../utils/purchasePage";
import { useLocation, useNavigate } from "react-router-dom";

/** ✅ UPDATED: include region */
type Company = {
  id: string;
  name: string;
  clubId?: string;
  city?: string;
  state?: string;
  phone?: string;
  region?: string;
  website?: string;
  url?: string;
};


type OfferApiItem =
  | string
  | {
      offerText?: string;
      text?: string;
      number_of_plays?: number;
      numberOfPlays?: number;
      vipRedeemedCount?: number;
      redeemedCount?: number;
      vipRedeemedLastAt?: string | null;
      unlimited?: boolean;

      days_of_the_week?: string | null;
      months?: string | null;

      region?: string | null;
    };

type Card = {
  hubdbRowId: string;
  clubId: string;
  company: Company;
  offers: OfferApiItem[];
  redemption?: {
    used: number;
    totalAllowed: number | null;
    remaining: number | null;
    unlimited?: boolean;
  };

  vipRedeemed?: boolean;
  vipRedeemedCount?: number;
  vipRedeemedLastAt?: string | null;
};

type OfferItem = {
  hubdbRowId: string;
  clubId: string;
  company: Company;
  offerNumber: number;
  offerText: string;

  numberOfPlays?: number;
  vipRedeemedCount?: number;
  vipRedeemedLastAt?: string | null;

  unlimited?: boolean;

  days_of_the_week?: string | null;
  months?: string | null;

  redemption?: {
    used: number;
    totalAllowed: number | null;
    remaining: number | null;
    unlimited?: boolean;
  };

  vipRedeemed?: boolean;
};

type AllOffersResp = {
  success: boolean;
  results: Card[];
  message?: string;
  meta?: { offer_year?: string };
};

type ContactResp = {
  success: boolean;
  contact?: {
    id: string;
    fullName: string;
    vipCardNumber: string;
  };
  message?: string;
};

type LocationState = { contactId?: string };

type ClubGroup = {
  clubId: string;
  company: Company;
  offers: OfferItem[];
};

const GroupList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { contactId } = (location.state || {}) as LocationState;

  const goLogin = () => navigate("/offers/login");

  const [contact, setContact] = useState<ContactResp["contact"] | null>(null);
  const [loadingContact, setLoadingContact] = useState(false);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [regions, setRegions] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("");

  const [allCards, setAllCards] = useState<Card[]>([]);
  const [offerYear, setOfferYear] = useState<string>(String(new Date().getFullYear()));
  const [loadingOffers, setLoadingOffers] = useState(false);

  const [companyId, setCompanyId] = useState<string>("");

  // ✅ Custom date picker state (ISO yyyy-mm-dd)

  const [redeemingKey, setRedeemingKey] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<OfferItem | null>(null);

  const [error, setError] = useState<string | null>(null);

  const parseMaybeJson = <T,>(x: any): T =>
    typeof x === "string" ? (JSON.parse(x) as T) : (x as T);

  const isLoggedIn = !!contactId && !!contact;

  const modalBusy =
    !!redeemingKey &&
    !!selectedOffer &&
    redeemingKey === `${selectedOffer.hubdbRowId}-${selectedOffer.offerNumber}`;


  const norm = (s?: string) =>
    String(s ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const splitCsv = (s?: string | null) =>
    String(s ?? "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  const MONTH_TO_NUMBER: Record<string, number> = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
  };

  const DAY_TO_INDEX: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  const getOfferValidityNow = (item?: OfferItem) => {
    if (!item)
      return {
        isValidNow: true,
        dayOk: true,
        monthOk: true,
        allowedDays: [] as string[],
        allowedMonths: [] as string[],
      };

    const now = new Date();
    const todayIdx = now.getDay();
    const thisMonthNum = now.getMonth() + 1;

    const allowedDays = splitCsv(item.days_of_the_week);
    const allowedMonths = splitCsv(item.months);

    const dayOk =
      allowedDays.length === 0
        ? true
        : allowedDays
            .map((d) => DAY_TO_INDEX[String(d).toLowerCase()])
            .filter((n) => typeof n === "number" && !Number.isNaN(n))
            .includes(todayIdx);

    const monthOk =
      allowedMonths.length === 0
        ? true
        : allowedMonths
            .map((m) => MONTH_TO_NUMBER[String(m).toLowerCase()])
            .filter((n) => typeof n === "number" && !Number.isNaN(n))
            .includes(thisMonthNum);

    return {
      isValidNow: dayOk && monthOk,
      dayOk,
      monthOk,
      allowedDays,
      allowedMonths,
    };
  };

  const formatUSPhone = (phone?: string) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith("1")) {
      const d = digits.slice(1);
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
    return phone;
  };

  const formatLocation = (c?: Company) => {
    const city = (c?.city || "").trim();
    const state = (c?.state || "").trim();
    if (city && state) return `${city}, ${state}`;
    return city || state || "";
  };

  const getOfferPlayStats = (item?: OfferItem) => {
    const isUnlimited = Boolean(item?.unlimited);

    const used =
      Number((item as any)?.vipRedeemedCount ?? 0) ||
      Number((item as any)?.redeemedCount ?? 0) ||
      Number(item?.redemption?.used ?? 0) ||
      0;

    if (isUnlimited) {
      return {
        isUnlimited: true as const,
        allowed: null as number | null,
        used,
        remaining: null as number | null,
      };
    }

    const playsFromOffer = Number(item?.numberOfPlays ?? 0);
    const playsFromCard = Number(item?.redemption?.totalAllowed ?? 0);

    const allowed =
      playsFromOffer > 0 ? playsFromOffer : playsFromCard > 0 ? playsFromCard : 1;

    const remaining = Math.max(0, allowed - used);

    return { isUnlimited: false as const, allowed, used, remaining };
  };

  /* ------------------------------------------------------------------ */
  /* Data loading                                                        */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const loadContact = async () => {
      if (!contactId) return;
      setLoadingContact(true);
      try {
        const resp = await getOffersContact(contactId);
        const data = parseMaybeJson<ContactResp>(resp);
        if (data?.success && data.contact) setContact(data.contact);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingContact(false);
      }
    };
    loadContact();
  }, [contactId]);

  useEffect(() => {
    const loadFilters = async () => {
      setLoadingCompanies(true);
      setError(null);

      try {
        const resp = await getOfferRegions();
        const data = parseMaybeJson<any>(resp);

        if (!data?.success) {
          setError(data?.message || "Failed to load filters.");
          setCompanies([]);
          setRegions([]);
          return;
        }

        const clubs: Company[] = Array.isArray(data.clubs)
          ? data.clubs.map((c: any) => ({
              id: "",
              clubId: String(c.clubId),
              name: String(c.name || "").trim(),
              region: String(c.region || "").trim(),
            }))
          : [];

        setCompanies(clubs);

        if (Array.isArray(data.regions)) {
          const cleaned: string[] = data.regions
            .map((r: unknown) => String(r ?? "").trim())
            .filter((r: string) => r.length > 0);

          const uniqueSorted: string[] = Array.from(new Set<string>(cleaned)).sort((a, b) =>
            a.localeCompare(b)
          );

          setRegions(uniqueSorted);
        } else {
          const derived = Array.from(
            new Set(
              clubs
                .map((c) => String(c.region || "").trim())
                .filter((r) => r.length > 0)
            )
          ).sort((a, b) => a.localeCompare(b));
          setRegions(derived);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load filters. Please try again.");
        setCompanies([]);
        setRegions([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    const loadAllOffers = async () => {
      setLoadingOffers(true);
      setError(null);

      try {
        const vip = contact?.vipCardNumber;
        const resp = await getAllOffers();
        const data = parseMaybeJson<AllOffersResp>(resp);

        if (!data?.success) {
          setError(data?.message || "Failed to load offers.");
          setAllCards([]);
          return;
        }

        setAllCards(data.results || []);
        if (data.meta?.offer_year) setOfferYear(data.meta.offer_year);
      } catch (e) {
        console.error(e);
        setError("Failed to load offers. Please try again.");
      } finally {
        setLoadingOffers(false);
      }
    };

    loadAllOffers();
  }, [contact?.vipCardNumber]);

  // Flatten
  const offerItems = useMemo<OfferItem[]>(() => {
    return allCards.flatMap((card) => {
      const offers = Array.isArray(card.offers) ? card.offers : [];

      const baseCompany: Company = {
        id: String(card.company?.id || "").trim(),
        name: String(card.company?.name || "").trim(),
        clubId: String(card.clubId || "").trim(),
        city: String(card.company?.city || "").trim(),
        state: String(card.company?.state || "").trim(),
        phone: String(card.company?.phone || "").trim(),
        region: String((card.company as any)?.region || "").trim(),
      };

      return offers.map((o, idx) => {
        const isObj = o && typeof o === "object";

        const offerText =
          typeof o === "string"
            ? o
            : String((o as any).offerText ?? (o as any).text ?? "").trim();

        const numberOfPlaysRaw =
          isObj ? (o as any).number_of_plays ?? (o as any).numberOfPlays : undefined;

        const numberOfPlays =
          numberOfPlaysRaw == null
            ? card.redemption?.totalAllowed == null
              ? undefined
              : Number(card.redemption?.totalAllowed || 0) || undefined
            : Number(numberOfPlaysRaw);

        const usedCountRaw =
          isObj ? (o as any).vipRedeemedCount ?? (o as any).redeemedCount : undefined;

        const usedCount =
          usedCountRaw == null
            ? Number(card.vipRedeemedCount ?? card.redemption?.used ?? 0)
            : Number(usedCountRaw);

        const unlimited = isObj ? Boolean((o as any).unlimited) : false;

        const days_of_the_week = isObj ? ((o as any).days_of_the_week ?? null) : null;
        const months = isObj ? ((o as any).months ?? null) : null;

        const offerRegion = isObj ? String((o as any).region ?? "").trim() : "";
        const region = offerRegion || baseCompany.region || "";

        return {
          hubdbRowId: String(card.hubdbRowId || "").trim(),
          clubId: String(card.clubId || "").trim(),
          company: { ...baseCompany, region },

          offerNumber: idx + 1,
          offerText,

          numberOfPlays,
          vipRedeemedCount: usedCount,
          vipRedeemedLastAt: isObj
            ? ((o as any).vipRedeemedLastAt ?? card.vipRedeemedLastAt ?? null)
            : card.vipRedeemedLastAt ?? null,

          unlimited,

          days_of_the_week,
          months,

          redemption: card.redemption,
          vipRedeemed: card.vipRedeemed,
        };
      });
    });
  }, [allCards]);

  // Filters on offers
  const filteredOffers = useMemo(() => {
    const selected = norm(selectedRegion);

    return offerItems.filter((item) => {
      // "companyId" select is actually clubId in your filter payload
      if (companyId && item.company?.id !== companyId && item.company?.clubId !== companyId) return false;

      if (selected) {
        const itemRegion = norm(item.company?.region || "");
        if (itemRegion !== selected) return false;
      }

      return true;
    });
  }, [offerItems, companyId, selectedRegion]);

  // ✅ NEW: group filtered offers by clubId so we render 1 card per club
  const groupedByClub = useMemo<ClubGroup[]>(() => {
    const map = new Map<string, ClubGroup>();

    for (const item of filteredOffers) {
      const clubId = String(item.clubId || item.company?.clubId || "").trim();
      if (!clubId) continue;

      const existing = map.get(clubId);
      if (!existing) {
        map.set(clubId, {
          clubId,
          company: item.company,
          offers: [item],
        });
      } else {
        existing.offers.push(item);
        // keep the best company data (first non-empty fields win)
        existing.company = {
          ...existing.company,
          ...Object.fromEntries(
            Object.entries(item.company || {}).filter(([_, v]) => v != null && String(v).trim() !== "")
          ),
        } as Company;
      }
    }

    // sort clubs by name, and offers by offerNumber
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        offers: [...g.offers].sort((a, b) => a.offerNumber - b.offerNumber),
      }))
      .sort((a, b) => (a.company?.name || "").localeCompare(b.company?.name || ""));
  }, [filteredOffers]);

  // Regions show as a collapsed list of accordions; each holds a Club | Offers table. HubDB stores one
  // offer across several rows (by month range), so each club lists its distinct offer texts once.
  const groupedByRegion = useMemo(() => {
    const map = new Map<string, { region: string; clubs: { clubId: string; name: string; offers: string[] }[] }>();
    for (const g of groupedByClub) {
      const region = String(g.company?.region || "").trim() || "Other";
      const offers = Array.from(new Set(g.offers.map((o) => o.offerText.trim()).filter(Boolean)));
      if (offers.length === 0) continue;
      const entry = map.get(region) ?? { region, clubs: [] };
      entry.clubs.push({ clubId: g.clubId, name: g.company?.name || "Course", offers });
      map.set(region, entry);
    }
    return Array.from(map.values()).sort((a, b) => a.region.localeCompare(b.region));
  }, [groupedByClub]);

  // A region or course picked in the filters opens its accordion.
  const [openRegions, setOpenRegions] = useState<Set<string>>(new Set());
  const isRegionOpen = (region: string) => !!companyId || !!selectedRegion || openRegions.has(region);
  const toggleRegion = (region: string) =>
    setOpenRegions((prev) => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
  const allOpen = groupedByRegion.length > 0 && groupedByRegion.every((r) => isRegionOpen(r.region));
  const toggleAll = () => setOpenRegions(allOpen ? new Set() : new Set(groupedByRegion.map((r) => r.region)));

  // Regions live on the offers (the club list has none), so a region's courses are the clubs with offers there.
  const coursesInRegion = useMemo(() => {
    if (!selectedRegion) return companies;
    const wanted = norm(selectedRegion);
    const clubIds = new Set(
      offerItems
        .filter((item) => norm(item.company?.region || "") === wanted)
        .map((item) => String(item.clubId || item.company?.clubId || ""))
    );
    return companies.filter((c) => clubIds.has(String(c.clubId)));
  }, [companies, offerItems, selectedRegion]);

  const openRedeemModal = (item: OfferItem) => {
    setError(null);
    setSelectedOffer(item);
    setModalOpen(true);
  };

  const confirmRedeem = async () => {
    if (!selectedOffer) return;
    if (modalBusy) return;

    const validity = getOfferValidityNow(selectedOffer);
    if (!validity.isValidNow) {
      setError("This offer isn’t valid today.");
      return;
    }

    if (!contactId) {
      setError("Missing contactId (OTP session). Please log in again.");
      return;
    }
    if (!contact?.vipCardNumber) {
      setError("VIP Card number missing. Please log in again.");
      return;
    }

    const stats = getOfferPlayStats(selectedOffer);

    if (!stats.isUnlimited && (stats.remaining ?? 0) <= 0) {
      setError("No redemptions remaining for this offer.");
      return;
    }

    const key = `${selectedOffer.hubdbRowId}-${selectedOffer.offerNumber}`;
    setRedeemingKey(key);

    try {
      const resp = await redeemOffer({
        contactId,
        companyId: selectedOffer.company.id,
        clubId: selectedOffer.clubId,
        hubdbRowId: selectedOffer.hubdbRowId,
        offerNumber: selectedOffer.offerNumber,
        vipCardNumber: contact.vipCardNumber,
        totalAllowed: stats.allowed ?? 0,
        offerText: selectedOffer.offerText,
      });

      const r = parseMaybeJson<any>(resp);
      if (!r?.success) {
        setError(r?.message || "Failed to redeem offer.");
        return;
      }

      // update counts in allCards so UI updates everywhere
      setAllCards((prev) =>
        prev.map((card) => {
          if (String(card.hubdbRowId).trim() !== String(selectedOffer.hubdbRowId).trim()) return card;

          const offers = Array.isArray(card.offers) ? card.offers : [];
          const idx = Number(selectedOffer.offerNumber) - 1;

          const nextOffers = offers.map((o, i) => {
            if (i !== idx) return o;

            if (o && typeof o === "object") {
              const currentUsed = Number((o as any).redeemedCount ?? (o as any).vipRedeemedCount ?? 0);
              return {
                ...o,
                redeemedCount: currentUsed + 1,
                vipRedeemedLastAt: new Date().toISOString(),
              };
            }

            return o;
          });

          const cardUsed = Number(card.redemption?.used ?? 0) || 0;
          const allowed = card.redemption?.totalAllowed;

          return {
            ...card,
            offers: nextOffers,
            redemption: card.redemption
              ? {
                  ...card.redemption,
                  used: cardUsed + 1,
                  remaining: allowed == null ? null : Math.max(0, Number(allowed) - (cardUsed + 1)),
                }
              : card.redemption,
            vipRedeemedCount: Number(card.vipRedeemedCount ?? 0) + 1,
            vipRedeemedLastAt: new Date().toISOString(),
          };
        })
      );

      setSelectedOffer((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          vipRedeemedCount: Number(prev.vipRedeemedCount ?? 0) + 1,
          vipRedeemedLastAt: new Date().toISOString(),
        };
      });

      setModalOpen(false);
      setSelectedOffer(null);
    } catch (e) {
      console.error(e);
      setError("Redeem failed. Please try again.");
    } finally {
      setRedeemingKey(null);
    }
  };

  const closeModal = () => {
    if (modalBusy) return;
    setModalOpen(false);
    setSelectedOffer(null);
  };

  const logout = () => {
    setContact(null);
    setModalOpen(false);
    setSelectedOffer(null);
    navigate("/offers");
  };

  return (
    <section className="container my-5">
      <div className="">
        <div className="card-body p-4">
          

          {/* Filters */}
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label small">Region</label>
              <select
                className="form-select"
                value={selectedRegion}
                onChange={(e) => {
                  setSelectedRegion(e.target.value);
                  setCompanyId("");
                }}
                disabled={loadingCompanies || regions.length === 0}
              >
                <option value="">
                  {loadingCompanies ? "Loading regions..." : regions.length === 0 ? "No regions" : "All regions"}
                </option>
                {regions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label small">Filter by Course</label>
              <select
                id="company"
                className="form-select"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                disabled={loadingCompanies}
              >
                <option value="">{loadingCompanies ? "Loading clubs..." : "All clubs"}</option>
                {coursesInRegion.map((c) => (
                  <option key={c.clubId} value={c.clubId}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <hr className="my-4" />

          {loadingOffers && <div className="text-muted">Loading offers…</div>}
          {!loadingOffers && groupedByClub.length === 0 && (
            <div className="text-muted">No offers found for the selected filters.</div>
          )}

          {!loadingOffers && groupedByRegion.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-3 small">
              <span className="text-muted">
                {groupedByClub.length} {groupedByClub.length === 1 ? "club" : "clubs"} in {groupedByRegion.length}{" "}
                {groupedByRegion.length === 1 ? "region" : "regions"}
              </span>
              <button type="button" className="vip-offers-toggle-all" onClick={toggleAll}>
                {allOpen ? "Collapse all" : "Expand all"}
              </button>
            </div>
          )}

          <div className="vip-offers-regions">
            {groupedByRegion.map(({ region, clubs }) => {
              const open = isRegionOpen(region);
              return (
                <div className="vip-offers-region" key={region}>
                  <button
                    type="button"
                    className="vip-offers-region__toggle"
                    aria-expanded={open}
                    onClick={() => toggleRegion(region)}
                  >
                    <span className="vip-offers-region__name">{region}</span>
                    <i className={`bi bi-chevron-down vip-offers-region__chevron${open ? " is-open" : ""}`} aria-hidden="true" />
                  </button>

                  {open && (
                    <table className="vip-offers-table">
                      <thead>
                        <tr>
                          <th className="vip-offers-table__club">Club</th>
                          <th>{offerYear} Offers</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clubs.map((club) => (
                          <tr key={club.clubId}>
                            <td className="vip-offers-table__club">{club.name}</td>
                            <td>
                              <ul className="vip-offers-table__offers">
                                {club.offers.map((text) => (
                                  <li key={text}>{text}</li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
          {error && <div className="text-danger mt-3">{error}</div>}
        </div>
      </div>

      {/* Modal (unchanged, still works per offer) */}
      {modalOpen && selectedOffer && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.5)", zIndex: 1040 }}
            onClick={modalBusy ? undefined : closeModal}
          />

          <div className="position-fixed top-50 start-50 translate-middle w-100" style={{ maxWidth: 640, zIndex: 1050 }}>
            <div className="card shadow border-0">
              <div
                className="px-4 py-3"
                style={{
                  backgroundColor: "#023822",
                  color: "#fff",
                  borderTopLeftRadius: 6,
                  borderTopRightRadius: 6,
                }}
              >
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <h5 className="mb-1" style={{ color: "#ffffff" }}>
                      {selectedOffer.company.name}
                    </h5>

                    {selectedOffer.company.region && (
                      <div className="small text-white-50">{selectedOffer.company.region}</div>
                    )}

                    <div className="small text-white-50">{formatLocation(selectedOffer.company) || ""}</div>

                    <div className="small text-white-50">
                      {selectedOffer.company.phone ? formatUSPhone(selectedOffer.company.phone) : ""}
                    </div>
                  </div>

                  <button
                    className="btn btn-sm border-0"
                    onClick={closeModal}
                    disabled={modalBusy}
                    style={{
                      color: "#fff",
                      opacity: 0.85,
                      background: "transparent",
                      lineHeight: 1,
                      fontSize: "1.15rem",
                    }}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="card-body p-4">
                <div style={{ whiteSpace: "pre-wrap" }}>{selectedOffer.offerText}</div>

                {/* ✅ Only show Used counts when logged in */}
                {isLoggedIn &&
                  (() => {
                    const v = getOfferValidityNow(selectedOffer);
                    if (!v.isValidNow) return null;

                    const stats = getOfferPlayStats(selectedOffer);

                    if (stats.isUnlimited) {
                      return (
                        <div className="mt-3">
                          <div className="small text-muted text-center">
                            <strong>Unlimited plays</strong>
                          </div>
                          <div className="small text-muted text-center">
                            Used: <strong>{stats.used}</strong>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="mt-3">
                        <div className="small text-muted text-center">
                          <strong>Used:</strong> {stats.used} of {stats.allowed}
                        </div>

                        {(stats.remaining ?? 0) <= 0 && (
                          <div className="alert alert-secondary py-2 mt-3 mb-0 text-center">
                            No redemptions remaining for this offer.
                          </div>
                        )}
                      </div>
                    );
                  })()}

                {/* validity banner */}
                {(() => {
                  const v = getOfferValidityNow(selectedOffer);
                  if (v.isValidNow) return null;

                  const dayMsg = v.allowedDays.length > 0 ? `Valid days: ${v.allowedDays.join(", ")}` : "";
                  const monthMsg = v.allowedMonths.length > 0 ? `Valid months: ${v.allowedMonths.join(", ")}` : "";

                  return (
                    <div className="alert alert-secondary py-2 mt-3 mb-0 text-center">
                      <div className="fw-semibold">This offer isn’t valid today.</div>
                      {(dayMsg || monthMsg) && (
                        <div className="small">
                          {dayMsg}
                          {dayMsg && monthMsg ? " • " : ""}
                          {monthMsg}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
                  <img src="/assets/tag.gif" className="img img-fluid" />
                </div>

                {modalBusy && (
                  <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
                    <div className="spinner-border spinner-border-sm" role="status" aria-label="Saving" />
                    <span className="small text-muted">Processing redemption</span>
                  </div>
                )}

                <div className="d-flex justify-content-center gap-3 mt-4 flex-wrap">
                  <button className="btn btn-outline-secondary" onClick={closeModal} disabled={modalBusy}>
                    Cancel
                  </button>

                  {isLoggedIn ? (
                    (() => {
                      const stats = getOfferPlayStats(selectedOffer);
                      const validity = getOfferValidityNow(selectedOffer);
                      const noRemaining = !stats.isUnlimited && (stats.remaining ?? 0) <= 0;

                      if (!validity.isValidNow) return null;

                      const disableRedeem = modalBusy || noRemaining;

                      return (
                        <button
                          className="btn fw-semibold"
                          onClick={confirmRedeem}
                          disabled={disableRedeem}
                          style={{
                            backgroundColor: "#8c6e2a",
                            color: "#ffffff",
                            border: "none",
                            opacity: disableRedeem ? 0.7 : 1,
                          }}
                        >
                          {modalBusy ? "Saving…" : noRemaining ? "No Plays Remaining" : "Redeem Now"}
                        </button>
                      );
                    })()
                  ) : (
                    <>
                      <button
                        className="btn btn-outline-primary fw-semibold"
                        onClick={goToPurchasePage}
                        disabled={modalBusy}
                      >
                        Purchase VIP Card
                      </button>

                      <button
                        className="btn fw-semibold"
                        onClick={goLogin}
                        disabled={modalBusy}
                        style={{
                          backgroundColor: "#C9A24D",
                          color: "#023822",
                          border: "none",
                          opacity: modalBusy ? 0.7 : 1,
                        }}
                      >
                        Login
                      </button>
                    </>
                  )}
                </div>

                {isLoggedIn && (
                  <div className="small text-muted mt-3 text-center">
                    Club Official Must Redeem
                  </div>
                )}

                {error && <div className="text-danger mt-3 text-center">{error}</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default GroupList;
